import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import ProjectNode from 'components/main/tree/ProjectNode';
import BaseTree from 'components/main/tree/BaseTree';

import {
  getAbsoluteRootTreeNode,
  getNewFolderType,
  getParents,
  isNullOrUndefined,
  showAlertSnackBar,
} from 'utils/Global';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { renameNodeDTO, TreeControlProps } from 'business/models/CustomModels';
import { TreeService } from 'business/services/TreeService';
import { TreeNodeDTO } from 'business/models/Models';
import { EPlateState } from 'enums/EPlatestate';
import useBroadcast, {
  CHANNEL_ACTIONS,
  CHANNEL_TYPES,
  MessageType,
} from 'business/broadcast';
import './style.scss';
import { containerNodeTypes, QUICK_ACCESS_NODE_ID } from './constants';
import { filterQuickAccessChildren } from './utils';
import { useTreeKeyboardHandler } from 'keyboardShortcuts/explorerHooks';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { useNavigate } from 'react-router-dom';
import { EagerLoadTreeDataSupplier } from 'business/tree/TreeDataSupplier';
export interface TreeControlState {
  isLoading: boolean;
  treenodes: any[];
  viewNodes: any;
  selectedNodeContainer: any;
  selectedNodes: any[]; // these nodes appear in center panel
  selectedExplorerNode: any; // these nodes appear in left tree
  breadcrumbs: any[];
  checkedExplorerNodes: number[];
  indeterminateExplorerNodes?: number[];
  isContextBackground?: boolean;
}

export const TreeControlDefaultState: TreeControlState = {
  isLoading: true,
  treenodes: [],
  viewNodes: {},
  selectedNodeContainer: null,
  selectedNodes: [],
  selectedExplorerNode: null,
  breadcrumbs: [],
  checkedExplorerNodes: [],
  isContextBackground: false,
};

const mainHeight = '100%';

const getTreeNodeById = (nodes, pTreeNodeId) => {
  let treeNode;
  for (let i = 0; i < nodes.length; i++) {
    let tempTreeNode = {};
    tempTreeNode = nodes[i];
    if (nodes[i].id === pTreeNodeId) {
      treeNode = tempTreeNode;
      break;
    } else if (nodes[i].children) {
      const _treeNode = getTreeNodeById(nodes[i].children, pTreeNodeId);

      if (_treeNode) {
        treeNode = _treeNode;
      }
    }
  }
  return treeNode;
};

const resolveSelectedNodesFromTree = (treeNodes, selectedNodes) => {
  if (!selectedNodes?.length) {
    return [];
  }

  return selectedNodes
    .map((selectedNode) => {
      if (!selectedNode?.id) {
        return selectedNode;
      }
      return getTreeNodeById(treeNodes, selectedNode.id);
    })
    .filter((n) => n);
};

const isNotFoundError = (error: any) =>
  error?.status === 404 || error?.statusCode === 404;

const toIdSet = (ids?: number[]) =>
  new Set((ids ?? []).map((id) => Number(id)).filter((id) => !Number.isNaN(id)));

const renameNodeInTree = (nodes: any[], id: number, newName: string): any[] => {
  if (!nodes?.length) {
    return nodes;
  }
  return nodes.map((n) => {
    if (n.id === id) {
      return { ...n, name: newName };
    }
    if (n.children?.length) {
      return { ...n, children: renameNodeInTree(n.children, id, newName) };
    }
    return n;
  });
};

const getChildren = (nodes, pTreeNodeId) => {
  let result;
  nodes.some(
    (node) =>
      (result =
        node.id === pTreeNodeId
          ? node.children
          : getChildren(node.children || [], pTreeNodeId)),
  );
  return result;
};

let crumbs = [];
const TreeControl = forwardRef((props: TreeControlProps, ref) => {
  const {
    setTreeControlState,
    treeControlState,
    shouldRenderNode,
    showQuickAccessNode = true,
    dataSupplier,
    setParentNode,
    commonTreeNodes,
    setCommonTreeNodes,
    isShowContext,
    onNodeSelect,
    onCheckboxChange,
    checkboxDisabled = false,
    backgroundColor = '#D9D9D9',
    allowedRootTreeNodeTypes,
    shouldListenToBroadcastedEvents = true,
    showRecycleBinNode = true,
    treeNodeIdToLoadTree,
    defaultFirstSelectedNode = ETreeNodeTypes.Projects,
    onNodeClickSelectPostAction,
    selectAllNodes,
    externalDragHandling = false,
    nodeDoubleClickHandler = (n) => {},
    isSearchEnabled = false,
    expandTargetNode = true,
  } = props;
  const [nodes, setNodes] = useState([]);
  const [loadedTreeNodes, setLoadedTreeNodes] = useState([]);
  const [expandedTreeNodeIds, setExpandedTreeNodesIds] = useState([]);
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);
  const [scrollToId, setScrolltoId] = useState<number>(null);
  const navigate = useNavigate();
  const latestSelectedIdRef = useRef<number | null>(null);
  const activeRequestRef = useRef<AbortController | null>(null);
  const deletedNodeIdsToIgnoreRef = useRef<Set<number>>(new Set());

  useImperativeHandle(
    ref,
    () => ({
      filterQuickAccess,
      onRefreshTreeNode,
      onRefreshTreeNodeAfterRename,
      onRefreshTreeNodeAfterForceUnlock,
      onRefreshTreeNodeAfterPlateStateChange,
      onRefreshTreeNodeAfterPlateBarcodeChange,
      OnDoubleClick,
      newNode,
      onClickNodeChildren,
      onRefreshTreeNodeAfterProteinVariantChange,
      generateAllTreeNodesById,
    }),
    [treeControlState, treeControlState.viewNodes, newNode, expandedTreeNodeIds],
  );
  const treeContainerRef = useRef(null);

  const generateAllTreeNodesById = useCallback(
    async (treeNodeId, isQuickAccessNode = false, signal?: AbortSignal) => {
      try {
        let selectedNode = {};
        const expandedTreeNodes = [];

        treeControlState.isLoading = true;
        setTreeControlState({
          ...treeControlState,
        });

        const treeService: TreeService = new TreeService();
        let nodes = [];
        let parentId = treeNodeId;
        let childNodes;
        try {
          childNodes = await treeService.getTreeNodesService(treeNodeId, null, signal);
          nodes = childNodes;
        } catch (error) {
          if (error.status === 404) {
            window.alert(
              'File not found. It may have been deleted or you dont have access.',
            );
            window.location.href = '/project';
            return;
          } else if (error.status === 403) {
            showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, error.message);
            navigate('/');
            return;
          } else {
            throw error;
          }
        }
        while (parentId != -1) {
          const tempParentId = await treeService.getParentIdByTreeNodeId(parentId);
          childNodes = await treeService.getTreeNodesService(tempParentId, null, signal);
          const parentNodeIndex = childNodes.findIndex((cn) => cn.id == parentId);
          const isTargetNode = parentId === treeNodeId;

          if (parentNodeIndex !== -1 && (!isTargetNode || expandTargetNode)) {
            childNodes[parentNodeIndex].children = nodes;
            childNodes[parentNodeIndex].state.expanded = true;
            expandedTreeNodes.push(childNodes[parentNodeIndex].id);
            nodes = childNodes;
          } else if (parentNodeIndex !== -1) {
            nodes = childNodes;
          }
          if (isTargetNode) {
            selectedNode = childNodes[parentNodeIndex];
          }

          parentId = tempParentId;
        }

        if (showQuickAccessNode) {
          if (isQuickAccessNode) {
            /**
             * since we already know that quickaccess order is first then we don't need to find the object.
             * we can just use first index of array and it will be quickaccess.
             */
            nodes[0] = { ...treeControlState.treenodes[0], state: { expanded: true } };
            expandedTreeNodes.push(QUICK_ACCESS_NODE_ID);
          }
          const quickAccessNodes = await treeService.getTreeNodesService(
            QUICK_ACCESS_NODE_ID,
            null,
            signal,
          );
          if (!nodes.length) {
            treeControlState.isLoading = false;
            setTreeControlState({
              ...treeControlState,
            });
            return;
          }
          nodes[0].children = quickAccessNodes;
        }
        if (!showRecycleBinNode) {
          nodes = nodes.filter((_node) => _node.type !== ETreeNodeTypes.RecycleBin);
        }
        if (!selectedNode) {
          window.location.href = '/';
          return;
        }
        treeControlState.treenodes = nodes;
        treeControlState.isLoading = false;
        treeControlState.selectedExplorerNode = selectedNode;

        setTreeControlState(cloneDeep(treeControlState));
        setExpandedTreeNodesIds(expandedTreeNodes);
        setLoadedTreeNodes([]);

        await onClickNodeChildren(selectedNode);
      } catch (e) {
        if (signal?.aborted) {
          return;
        }
        if (e?.status === 404) {
          window.alert(
            'File not found. It may have been deleted or you dont have access.',
          );
          window.location.href = '/project';
          return;
        }
        throw e;
      }
    },
    [treeControlState, treeControlState.treenodes, treeControlState.checkedExplorerNodes],
  );

  async function fetchData(signal?: AbortSignal) {
    crumbs = [];
    let data = [];
    try {
      if (dataSupplier) {
        treeControlState.isLoading = true;
        const supplier = new dataSupplier();
        data = await supplier.getTreeNodes(null, allowedRootTreeNodeTypes, signal);
        if (data) {
          treeControlState.isLoading = false;
        }

        const treeService: TreeService = new TreeService();
        if (showQuickAccessNode && data?.length) {
          const quickAccessNodes = await treeService.getTreeNodesService(
            QUICK_ACCESS_NODE_ID,
            null,
            signal,
          );
          data[0].children = quickAccessNodes;
        }

        if (!showRecycleBinNode) {
          data = data.filter((_node) => _node.type !== ETreeNodeTypes.RecycleBin);
        }
        treeControlState.treenodes = data;
        setTreeControlState({ ...treeControlState });
        const projectNode = data?.find((item) => item.name === defaultFirstSelectedNode);
        if (projectNode) {
          onClickNodeChildren(projectNode);
          OnDoubleClick(projectNode);
        }
      }
    } catch (error) {
      setTreeControlState({ ...treeControlState, isLoading: false });
      if (signal?.aborted) {
        return;
      }
      throw error;
    }
  }

  const handleBroadcastMessageRecieve = useCallback(
    async (messageEvent: MessageEvent<MessageType<TreeNodeDTO>>) => {
      const { action, payload: node } = messageEvent.data;
      if (
        !(node as { parentId?: number }).parentId &&
        action !== CHANNEL_ACTIONS.EXPLORER_RENAME_NODE &&
        action !== CHANNEL_ACTIONS.EXPLORER_DELETE_NODE &&
        action !== CHANNEL_ACTIONS.EXPLORER_RESTORE_NODE &&
        action !== CHANNEL_ACTIONS.EXPLORER_ADD_NODE
      ) {
        return;
      }

      switch (action) {
        case CHANNEL_ACTIONS.EXPLORER_DELETE_NODE: {
          const { parentId, deletedNodeIds } = node as {
            parentId: number;
            deletedNodeIds?: number[];
          };
          const deletedIdSet = toIdSet(deletedNodeIds);
          if (deletedIdSet.size && showQuickAccessNode) {
            filterQuickAccess([...deletedIdSet]);
          }
          if (
            ((!dataSupplier && allowedRootTreeNodeTypes?.length) ||
              dataSupplier === EagerLoadTreeDataSupplier) &&
            deletedIdSet.size
          ) {
            const idsToRemove = [...deletedIdSet];
            const removeNodesById = (nodes: any[], idsToRemove: number[]): any[] =>
              nodes
                .filter((n) => !idsToRemove.includes(Number(n.id)))
                .map((n) => ({
                  ...n,
                  children: n.children
                    ? removeNodesById(n.children, idsToRemove)
                    : n.children,
                }));
            setTreeControlState((prev) => ({
              ...prev,
              treenodes: removeNodesById(prev.treenodes, idsToRemove),
              viewNodes: Array.isArray(prev.viewNodes)
                ? prev.viewNodes.filter((n) => !deletedIdSet.has(Number(n.id)))
                : prev.viewNodes,
              selectedNodes: prev.selectedNodes?.filter(
                (n) => !deletedIdSet.has(Number(n.id)),
              ),
              selectedExplorerNode: deletedIdSet.has(
                Number(prev.selectedExplorerNode?.id),
              )
                ? null
                : prev.selectedExplorerNode,
            }));
            break;
          }
          if (parentId == null || Number.isNaN(Number(parentId))) {
            break;
          }
          try {
            let parentNode = getTreeNodeById(treeControlState.treenodes, parentId);
            if (!parentNode) {
              const projectsNodeId = await getAbsoluteRootTreeNode(parentId);
              parentNode = getTreeNodeById(treeControlState.treenodes, projectsNodeId);
            }
            if (parentNode && !deletedIdSet.has(Number(parentNode.id))) {
              const isExplorerDeleted = deletedIdSet.has(
                Number(treeControlState.selectedExplorerNode?.id),
              );
              await onRefreshTreeNode(
                parentNode,
                treeControlState.selectedExplorerNode?.id === parentId,
                null,
                null,
                isExplorerDeleted,
                [...deletedIdSet],
              );
            }
          } catch (error) {
            if (isNotFoundError(error)) {
              break;
            }
            throw error;
          }
          break;
        }
        case CHANNEL_ACTIONS.EXPLORER_RESTORE_NODE: {
          // Eager layers tree is refreshed by useEagerLoadTreeNodes. Refreshing
          // only immediate children here would drop nested proteins/folders.
          if (
            (!dataSupplier && allowedRootTreeNodeTypes?.length) ||
            dataSupplier === EagerLoadTreeDataSupplier
          ) {
            break;
          }
          const { parentIds } = messageEvent.data.payload as unknown as {
            parentIds: number[];
          };
          try {
            for (const restoreParentId of parentIds ?? []) {
              if (restoreParentId == null || Number.isNaN(Number(restoreParentId))) {
                continue;
              }
              let parentNode = getTreeNodeById(
                treeControlState.treenodes,
                restoreParentId,
              );
              if (!parentNode) {
                const projectsNodeId = await getAbsoluteRootTreeNode(restoreParentId);
                parentNode = getTreeNodeById(treeControlState.treenodes, projectsNodeId);
              }
              if (parentNode) {
                await onRefreshTreeNode(
                  parentNode,
                  treeControlState.selectedExplorerNode?.id === restoreParentId,
                );
              }
            }
          } catch (error) {
            if (isNotFoundError(error)) {
              break;
            }
            throw error;
          }
          break;
        }
        case CHANNEL_ACTIONS.EXPLORER_ADD_NODE: {
          if (
            (!dataSupplier && allowedRootTreeNodeTypes?.length) ||
            dataSupplier === EagerLoadTreeDataSupplier
          ) {
            break;
          }
          const { parentId: addParentId } = messageEvent.data.payload as unknown as {
            parentId: number;
          };
          if (!addParentId || Number.isNaN(Number(addParentId))) {
            break;
          }
          try {
            let parentNode = getTreeNodeById(treeControlState.treenodes, addParentId);
            if (!parentNode) {
              const projectsNodeId = await getAbsoluteRootTreeNode(addParentId);
              parentNode = getTreeNodeById(treeControlState.treenodes, projectsNodeId);
            }
            if (parentNode) {
              await onRefreshTreeNode(
                parentNode,
                treeControlState.selectedExplorerNode?.id === addParentId,
              );
            }
          } catch (error) {
            if (isNotFoundError(error)) {
              break;
            }
            throw error;
          }
          break;
        }
        case CHANNEL_ACTIONS.EXPLORER_RENAME_NODE:
          onRefreshTreeNodeAfterRename({ id: node.id, newName: node.name }, false);
          break;

        default:
          return;
      }
    },
    [treeControlState, showQuickAccessNode],
  );

  useEffect(() => {
    if (explorerBroadcastChannel && shouldListenToBroadcastedEvents) {
      explorerBroadcastChannel.onmessage = handleBroadcastMessageRecieve;
    }
  }, [explorerBroadcastChannel, handleBroadcastMessageRecieve]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    if (
      dataSupplier === EagerLoadTreeDataSupplier &&
      treeControlState.treenodes.length > 0
    ) {
      return;
    }
    if (treeNodeIdToLoadTree && treeNodeIdToLoadTree !== -1) {
      generateAllTreeNodesById(treeNodeIdToLoadTree, null, signal);
    } else {
      fetchData(signal);
    }

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (treeControlState.selectedNodeContainer) {
      const parents = getParents(nodes, treeControlState.selectedNodeContainer.id);
      if (isNullOrUndefined(parents)) {
        return;
      }
      const parentsWithChildrens = parents.filter((p) => !isNullOrUndefined(p.children));
      const state = treeControlState;
      state.viewNodes = parentsWithChildrens[0].children;
      setTreeControlState({ ...state });
    }
  }, []);

  async function filterQuickAccess(treeNodeIds: Array<number>) {
    if (!showQuickAccessNode) {
      return;
    }
    const state = { ...treeControlState };
    const filteredQaNodes = filterQuickAccessChildren(
      state.treenodes?.[0]?.children,
      treeNodeIds,
    );
    if (!filteredQaNodes) {
      return;
    }
    state.treenodes[0] = { ...state.treenodes[0], children: filteredQaNodes };
    setTreeControlState(state);
  }

  async function onRefreshTreeNode(
    node,
    shouldUpdateViewNodes = true,
    newChildrenId = null,
    selectedNodesToSet = null, // it should be a []
    selectParentExplorerNode = false,
    deletedNodeIds: number[] = [],
  ) {
    // TO DO: fix proper state management through redux later as current states are incosistent
    let treeState = null;
    const _selectedNodesToSet = selectedNodesToSet;
    deletedNodeIdsToIgnoreRef.current = toIdSet(deletedNodeIds);

    if (!treeState) {
      treeState = treeControlState;
    }

    if (node?.id == null || deletedNodeIdsToIgnoreRef.current.has(Number(node.id))) {
      deletedNodeIdsToIgnoreRef.current = new Set();
      return;
    }

    if (node.id === QUICK_ACCESS_NODE_ID) {
      const treeService = new TreeService();
      const quickAccessNodes = await treeService.getTreeNodesService(
        QUICK_ACCESS_NODE_ID,
      );
      const updatedTreenodes = treeState.treenodes.map((tn, index) => {
        if (index === 0 || tn.id === QUICK_ACCESS_NODE_ID) {
          return { ...tn, children: quickAccessNodes };
        }
        return tn;
      });
      const refreshedSelectedNodes = resolveSelectedNodesFromTree(
        updatedTreenodes,
        treeState.selectedNodes,
      );
      const refreshedExplorerNode =
        getTreeNodeById(updatedTreenodes, treeState.selectedExplorerNode?.id) ?? null;
      let finalSelectedNodes = _selectedNodesToSet
        ? resolveSelectedNodesFromTree(updatedTreenodes, _selectedNodesToSet)
        : refreshedSelectedNodes;
      if (!finalSelectedNodes?.length && refreshedExplorerNode) {
        finalSelectedNodes = [refreshedExplorerNode];
      }

      setTreeControlState({
        ...treeState,
        treenodes: updatedTreenodes,
        viewNodes: shouldUpdateViewNodes ? quickAccessNodes : treeState.viewNodes,
        selectedExplorerNode: refreshedExplorerNode,
        selectedNodes: finalSelectedNodes,
        breadcrumbs: refreshedExplorerNode
          ? getParents(updatedTreenodes, refreshedExplorerNode.id)
          : treeState.breadcrumbs,
      });
      deletedNodeIdsToIgnoreRef.current = new Set();
      return;
    }

    try {
      const nodes = await updateChildren(node, treeState.treenodes);
      const curNode = getTreeNodeById(nodes, node.id);

      if (shouldUpdateViewNodes) {
        treeState.viewNodes = curNode?.children;
      }
      treeState.treenodes = nodes;

      if (newChildrenId && curNode) {
        curNode.state.expanded = true;
        setExpandedTreeNodesIds([...expandedTreeNodeIds, node.id]);
        const newNode = getTreeNodeById(nodes, newChildrenId);
        const breadcrumbs = getParents(treeState.treenodes, newNode.id);
        treeState.selectedExplorerNode = newNode;
        treeState.selectedNodes = [newNode];
        treeState.viewNodes = [];
        treeState.breadcrumbs = breadcrumbs;
        setScrolltoId(newNode.id);
        if (setParentNode) {
          setParentNode(newNode);
        }
      }

      let explorerNodeToSelect = treeState.selectedExplorerNode;
      if (selectParentExplorerNode && curNode) {
        explorerNodeToSelect = curNode;
        treeState.breadcrumbs = getParents(nodes, curNode.id);
      } else {
        const explorerStillExists = explorerNodeToSelect?.id
          ? !!getTreeNodeById(nodes, explorerNodeToSelect.id)
          : true;
        if (!explorerStillExists && curNode) {
          explorerNodeToSelect = curNode;
          treeState.breadcrumbs = getParents(nodes, curNode.id);
        }
      }

      const refreshedExplorerNode = explorerNodeToSelect?.id
        ? getTreeNodeById(nodes, explorerNodeToSelect.id)
        : explorerNodeToSelect;
      let finalSelectedNodes = _selectedNodesToSet
        ? resolveSelectedNodesFromTree(nodes, _selectedNodesToSet)
        : resolveSelectedNodesFromTree(nodes, treeState.selectedNodes);

      if (selectParentExplorerNode && refreshedExplorerNode) {
        finalSelectedNodes = [refreshedExplorerNode];
      } else if (!finalSelectedNodes?.length && refreshedExplorerNode) {
        finalSelectedNodes = [refreshedExplorerNode];
      }

      setTreeControlState({
        ...treeState,
        selectedExplorerNode: refreshedExplorerNode,
        selectedNodes: finalSelectedNodes,
      });
    } catch (error) {
      if (isNotFoundError(error)) {
        return;
      }
      throw error;
    } finally {
      deletedNodeIdsToIgnoreRef.current = new Set();
    }
  }

  const onRefreshTreeNodeAfterRename = useCallback(
    (node: renameNodeDTO, shouldBroadcast = true) => {
      setTreeControlState((prev) => {
        if (!getTreeNodeById(prev.treenodes, node.id)) {
          return prev;
        }
        const updateNodeInArray = (arr: any[]) =>
          Array.isArray(arr)
            ? arr.map((n) => (n?.id === node.id ? { ...n, name: node.newName } : n))
            : arr;

        return {
          ...prev,
          treenodes: renameNodeInTree(prev.treenodes, node.id, node.newName),
          breadcrumbs: prev.breadcrumbs?.map((b) =>
            b.id === node.id ? { ...b, name: node.newName } : b,
          ),
          viewNodes: updateNodeInArray(prev.viewNodes),
          selectedNodes: updateNodeInArray(prev.selectedNodes),
          selectedExplorerNode:
            prev.selectedExplorerNode?.id === node.id
              ? { ...prev.selectedExplorerNode, name: node.newName }
              : prev.selectedExplorerNode,
        };
      });

      if (shouldBroadcast && explorerBroadcastChannel) {
        explorerBroadcastChannel.postMessage({
          action: CHANNEL_ACTIONS.EXPLORER_RENAME_NODE,
          payload: { id: node.id, name: node.newName },
        });
      }
    },
    [explorerBroadcastChannel],
  );

  const onRefreshTreeNodeAfterForceUnlock = async (node) => {
    const newNode = getTreeNodeById(treeControlState.treenodes, node.id);
    newNode.locked = false;
    setTreeControlState({ ...treeControlState, selectedNode: { ...newNode } });
  };
  const onRefreshTreeNodeAfterPlateStateChange = async (
    node,
    plateState: EPlateState,
    experimentState: string,
    plateIdsToDispense: number[],
  ) => {
    const newNode: TreeNodeDTO = getTreeNodeById(treeControlState.treenodes, node.id);
    if (newNode?.extra?.plateState >= 0) {
      newNode.extra.plateState = plateState;
    }
    if (!plateIdsToDispense?.length) {
      newNode?.extra?.plates?.map((plate) => (plate.plateState = plateState));
    } else {
      newNode?.extra?.plates?.map(
        (plate) =>
          (plate.plateState = plateIdsToDispense?.includes(plate.id)
            ? plateState
            : plate.plateState),
      );
    }
    if (newNode?.extra?.state) {
      newNode.extra.state = experimentState;
    }
    setTreeControlState({ ...treeControlState, selectedNodes: [{ ...newNode }] });
  };

  const onRefreshTreeNodeAfterPlateBarcodeChange = async (
    node,
    plateId: number,
    barcode: string,
  ) => {
    const newNode: TreeNodeDTO = getTreeNodeById(treeControlState.treenodes, node.id);
    if (newNode?.extra?.plates) {
      newNode.extra.plates = newNode.extra.plates?.map((plate) =>
        plate.id === plateId ? { ...plate, barcode } : plate,
      );
    }
    setTreeControlState({ ...treeControlState, selectedNodes: [{ ...newNode }] });
  };

  const onRefreshTreeNodeAfterProteinVariantChange = async (
    treeNodeId,
    isVariantExists,
  ) => {
    const newNode = getTreeNodeById(treeControlState.treenodes, treeNodeId);
    if (newNode?.extra) {
      newNode.extra.isVariantExist = isVariantExists;
    }
    setTreeControlState((prev) => ({
      ...prev,
      selectedNodes: prev.selectedNodes.map((node) =>
        node.id === treeNodeId
          ? { ...node, extra: { ...node.extra, isVariantExist: isVariantExists } }
          : node,
      ),
    }));
  };

  async function updateChildren(node, treeNodesCopy) {
    const usesAllowedTypesOnly =
      allowedRootTreeNodeTypes?.length &&
      (!dataSupplier || dataSupplier === EagerLoadTreeDataSupplier);
    if (!dataSupplier && !usesAllowedTypesOnly) {
      return treeNodesCopy;
    }
    if (node?.id == null || deletedNodeIdsToIgnoreRef.current.has(Number(node.id))) {
      return treeNodesCopy;
    }
    let data;
    try {
      if (usesAllowedTypesOnly) {
        const treeService = new TreeService();
        data = await treeService.getTreeNodesService(node.id, allowedRootTreeNodeTypes);
        data = data?.filter((n) => allowedRootTreeNodeTypes.includes(n.type)) ?? data;
      } else {
        const supplier = new dataSupplier();
        data = await supplier.getTreeNodes(node.id);
      }
    } catch (error) {
      if (isNotFoundError(error)) {
        return treeNodesCopy;
      }
      throw error;
    }
    const deletedSet = deletedNodeIdsToIgnoreRef.current;
    if (deletedSet.size && Array.isArray(data)) {
      data = data.filter((tn) => !deletedSet.has(Number(tn.id)));
    }
    const nodes = generateArrayForNewChildrens(
      treeControlState.treenodes,
      data,
      node.id,
      expandedTreeNodeIds.find((id) => id === node.id) ? true : null,
      false,
    );
    treeNodesCopy = [...nodes];
    if (!isNullOrUndefined(data) && data.length > 0) {
      await Promise.all(
        data.map(async (tn) => {
          if (deletedSet.has(Number(tn.id))) {
            return;
          }
          const isFound = loadedTreeNodes.find((id) => id === tn.id);
          if (isFound) {
            await updateChildren(tn, treeNodesCopy);
          }
        }),
      );
    }
    return treeNodesCopy;
  }

  function OnClickSelect(node) {
    if (onNodeClickSelectPostAction) {
      onNodeClickSelectPostAction(node);
    }
    const state = treeControlState;
    const nodeId = node.id;
    if (setParentNode) {
      setParentNode(node);
    }
    crumbs = getParents(treeControlState.treenodes, nodeId);
    if (!node.locked) {
      const cNodes = getChildren(treeControlState.treenodes, nodeId);
      if (cNodes?.length) {
        state.viewNodes = cNodes;
        state.selectedExplorerNode = node;
        state.breadcrumbs = crumbs;
      } else if (
        // selected node doesn't have any children and we should go one level up to select the nodes from its parents
        // Like experiment is selected then we will be displaying children of experiment's parent
        crumbs?.length >= 2 &&
        node.type !== ETreeNodeTypes.Project &&
        node.type !== ETreeNodeTypes.ProjectFolder &&
        node.type !== ETreeNodeTypes.Projects &&
        node.type !== ETreeNodeTypes.ProjectsFolder &&
        node.type !== ETreeNodeTypes.ScreensFolder &&
        node.type !== ETreeNodeTypes.SearchesFolder &&
        node.type !== ETreeNodeTypes.SynchrotronTripv4Folder
      ) {
        state.viewNodes = crumbs[1].children;
        state.selectedExplorerNode = node;
        state.breadcrumbs = crumbs;
      } else {
        state.viewNodes = [];
      }
    } else {
      state.viewNodes = [];
    }
    state.selectedNodes = [node];
    state.breadcrumbs = crumbs;
    state.selectedExplorerNode = node;
    state.isLoading = false;
    state.isContextBackground = false;
    setTreeControlState({ ...state });
  }

  function generateArrayForNewChildrens(
    nodes,
    newChildren,
    pTreeNodeId,
    expanded,
    isUpdateViewNodes = true,
  ) {
    interface Node {
      children: React.ReactNode;
      state: any;
    }
    const array = [];
    for (let i = 0; i < nodes.length; i++) {
      let node: Node = {
        children: '',
        state: '',
      };
      node = nodes[i];
      if (nodes[i].children) {
        node.children = generateArrayForNewChildrens(
          nodes[i].children,
          newChildren,
          pTreeNodeId,
          expanded,
          isUpdateViewNodes,
        );
      }
      if (nodes[i].id === pTreeNodeId) {
        node.children = newChildren;
        node.state = { expanded };

        if (isUpdateViewNodes) {
          const state = treeControlState;
          state.viewNodes = node.children;
        }
      }
      array.push(node);
    }
    return array;
  }

  async function OnDoubleClick(node, expandAndSelect = true, expandOnSelection = true) {
    if (!node?.id) {
      return;
    }
    if (node?.extra?.quickAccessMeta?.originalId) {
      generateAllTreeNodesById(node.extra.quickAccessMeta.originalId, true);
      return;
    }
    latestSelectedIdRef.current = node.id;
    // cancel previous pending request
    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
    }

    const controller = new AbortController();
    activeRequestRef.current = controller;
    const signal = controller.signal;

    const state = treeControlState;
    let treeNodes = treeControlState.treenodes;
    const expanded = expandOnSelection ? node?.state?.expanded || false : true;
    const html = document.getElementsByTagName('html')[0];
    let data = [];
    html.style.cssText = '--cursor: wait';
    const { id } = node;
    if (id === undefined) {
      return;
    }
    if (expandOnSelection) {
      if (expandedTreeNodeIds.find((tnIds) => tnIds === node.id)) {
        setExpandedTreeNodesIds((prev) => prev.filter((x) => x !== id));
      } else {
        setExpandedTreeNodesIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }

    if (node.children && node.children.length > 0) {
      html.style.cssText = '--cursor: default';
      const nodes = generateArrayForNewChildrens(
        treeControlState.treenodes,
        node.children,
        id,
        !expanded,
        expandAndSelect,
      );

      if (expandAndSelect) {
        treeNodes = nodes;
        OnClickSelect(node);
      }
      return;
    }

    for (let iter = 0; iter < loadedTreeNodes.length; iter++) {
      if (loadedTreeNodes[iter] === id) {
        html.style.cssText = '--cursor: default';
        if (expandAndSelect) {
          treeNodes = loadedTreeNodes[iter];
          OnClickSelect(node);
        }
        return;
      }
    }

    if (dataSupplier) {
      const supplier = new dataSupplier();
      try {
        data = await supplier.getTreeNodes(id, [], signal);
      } catch (err) {
        if (signal.aborted || isNotFoundError(err)) {
          return;
        }
        throw err;
      } finally {
        html.style.removeProperty('--cursor');
      }
    }
    if (!signal.aborted) {
      loadedTreeNodes.push(id);
    }

    html.style.cssText = '--cursor: default';

    const onlyProjectsAndFolder = data.filter(
      (node) =>
        node.type == ETreeNodeTypes.Project ||
        node.type == ETreeNodeTypes.ProjectsFolder ||
        node.type == ETreeNodeTypes.ScreensFolder ||
        node.type == ETreeNodeTypes.ProjectFolder ||
        node.type == ETreeNodeTypes.QuickAccess,
    );
    let deepCopyNodes = [];
    if (!commonTreeNodes || commonTreeNodes?.length == 0) {
      deepCopyNodes = cloneDeep(treeNodes);
    } else {
      deepCopyNodes = cloneDeep(commonTreeNodes);
    }
    const filteredNewlyGenerateNodes = generateArrayForNewChildrens(
      deepCopyNodes,
      [...onlyProjectsAndFolder],
      id,
      !expanded,
      expandAndSelect,
    );
    const newlyGeneratedNodes = generateArrayForNewChildrens(
      treeNodes,
      data,
      id,
      !expanded,
      expandAndSelect,
    );
    setNodes(newlyGeneratedNodes);
    if (setCommonTreeNodes) {
      setCommonTreeNodes(filteredNewlyGenerateNodes);
    }
    if (expandAndSelect) {
      treeNodes = newlyGeneratedNodes;
      crumbs = getParents(treeNodes, id);
      if (expandOnSelection) {
        setExpandedTreeNodesIds((prev) => [
          ...new Set([...prev, ...(crumbs?.map((item) => item.id) ?? [])]),
        ]);
      }
      if (latestSelectedIdRef.current !== node.id) {
        return;
      }
      OnClickSelect(node);
      state.breadcrumbs = crumbs;
      state.selectedExplorerNode = node;
    }
    setTreeControlState({ ...state });
  }
  function onClickNodeChildren(selNode) {
    if (selNode?.extra?.quickAccessMeta?.originalId) {
      return;
    }
    OnClickSelect(selNode);
  }

  async function newNode(newChildren: TreeNodeDTO, children, pTreeNodeId) {
    const node: TreeNodeDTO = getTreeNodeById(treeControlState.treenodes, pTreeNodeId);
    if (!node) {
      return null;
    }
    if (containerNodeTypes.includes(newChildren.type as ETreeNodeTypes)) {
      node.hasContainerNode = true;
      await onRefreshTreeNode(node, true, newChildren.id);
    } else {
      await onRefreshTreeNode(node);
    }
    explorerBroadcastChannel?.postMessage({
      action: CHANNEL_ACTIONS.EXPLORER_ADD_NODE,
      payload: { parentId: pTreeNodeId },
    });
    return null;
  }

  const generateNode = useCallback(
    (node, rest) => {
      return (
        <ProjectNode
          data={node}
          selectedNode={treeControlState.selectedExplorerNode}
          rest={rest}
          newFolderType={getNewFolderType(treeControlState.treenodes, node)}
          newNode={newNode}
          onClickHandler={OnClickSelect}
          openDocument={(expandAndSelect = true) => {
            OnDoubleClick(
              getTreeNodeById(treeControlState.treenodes, node.id),
              expandAndSelect,
            );
            if (onNodeSelect && expandAndSelect) {
              onNodeSelect();
            }
          }}
          onContextMenuClick={() => {}}
          onClickNodeChildren={onClickNodeChildren}
          onRefreshTreeNode={onRefreshTreeNode}
          isShowContext={isShowContext}
          onCheckboxChange={onCheckboxChange}
          checkboxDisabled={checkboxDisabled}
          treeControlState={treeControlState}
          backgroundColor={backgroundColor}
          externalDragHandling={externalDragHandling}
          onDoubleClick={nodeDoubleClickHandler}
        />
      );
    },
    [
      treeControlState.selectedExplorerNode,
      treeControlState.treenodes,
      isShowContext,
      treeControlState.viewNodes,
      expandedTreeNodeIds,
      TreeControlDefaultState.checkedExplorerNodes,
      treeControlState.selectedNodes,
      onCheckboxChange,
      checkboxDisabled,
      onNodeSelect,
      nodeDoubleClickHandler,
    ],
  );

  const handleChange = useCallback(
    (nodes) => setTreeControlState((prvState) => ({ ...prvState, treenodes: nodes })),
    [],
  );

  useEffect(() => {
    setTreeControlState(treeControlState);
  }, []);

  useTreeKeyboardHandler({
    treeContainerRef: treeContainerRef,
    selectedExplorerNode: treeControlState?.selectedExplorerNode,
    treenodes: treeControlState?.treenodes,
    onSelectNode: OnClickSelect,
    onToggleExpand: OnDoubleClick,
    getTreeNodeById: getTreeNodeById,
    onCtrlAPress: selectAllNodes,
  });

  return (
    <BaseTree
      containerRef={treeContainerRef}
      mainHeight={mainHeight}
      handleChange={handleChange}
      treenodes={treeControlState.treenodes}
      generateNode={generateNode}
      shouldRenderNode={shouldRenderNode}
      expandedTreeNodeIds={expandedTreeNodeIds}
      treeControlState={treeControlState}
      backgroundColor={backgroundColor}
      scrollToId={scrollToId}
      isLoading={treeControlState.isLoading}
      isSearchEnabled={isSearchEnabled}
    />
  );
});
export default TreeControl;
